import { useElevenVoices } from "./Voices.js";
import { useEffect, useMemo, useState } from "react";
import {
  ENTITY_KINDS,
  XAI_VOICES,
  type CampaignBible,
  type EntityKind,
  type GlobalMemoryCategory,
  type GlobalMemoryEntry,
  type Memory,
  type MemoryInput,
  type MemoryResponse,
  type QuestStatus,
} from "@dm/shared";
import { api } from "./api.js";
import { useGame } from "./game.js";

const KIND_LABEL: Record<EntityKind, string> = { npc: "NPCs", place: "Places", faction: "Factions", item: "Items & artifacts", quest: "Quests", note: "Other notes" };
const KIND_ONE: Record<EntityKind, string> = { npc: "NPC", place: "Place", faction: "Faction", item: "Item", quest: "Quest", note: "Note" };

const BIBLE_ROWS: Array<{ key: keyof Omit<CampaignBible, "updatedAt" | "updatedBy">; label: string; hint: string; staffOnly?: boolean }> = [
  { key: "setting", label: "Setting", hint: "World, region, starting town…" },
  { key: "tone", label: "Tone", hint: "Heroic, grim, comedic, horror…" },
  { key: "currentState", label: "Current state", hint: "What is true right now (where the party is, who was rescued…). The DM reads this first; the scribe rewrites it" },
  { key: "objectives", label: "Current quest / objectives", hint: "What the party is trying to do right now" },
  { key: "partyGoals", label: "Party goals", hint: "Long-term goals of the party and characters" },
  { key: "threads", label: "Open threads & hooks", hint: "Unresolved mysteries, promises, rumours (one per line)" },
  { key: "decisions", label: "Important decisions", hint: "Choices with consequences" },
  { key: "timeline", label: "Timeline", hint: "Key events, oldest first" },
  { key: "dmSecrets", label: "DM-only secrets", hint: "Never shown to players; the DM won't reveal them", staffOnly: true },
];

/**
 * Campaign memory (the start of the campaign wiki). Host and Co-hosts see and edit everything; players see the
 * player-safe version: no DM secrets, only NPCs/places/etc. the party has met, quests and session recaps.
 */
export function MemoryPage() {
  const { campaign, fail, sessionsVersion } = useGame();
  const [data, setData] = useState<MemoryResponse | null>(null);
  const [filter, setFilter] = useState("");
  const load = () => api.memory(campaign.id).then(setData, fail);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.id, sessionsVersion]);

  if (!data) return <main className="muted">Loading…</main>;
  const staff = data.canEdit;
  const q = filter.trim().toLowerCase();
  const match = (m: Memory) => !q || `${m.title} ${m.content} ${m.attitude} ${staff ? m.secrets : ""}`.toLowerCase().includes(q);

  return (
    <main className="memory-page">
      <p className="muted small">
        {staff
          ? "Everything the DM remembers about this campaign. The DM's scribe keeps it up to date during and after each session; you can edit anything. Players see a safe version without DM-only secrets or things they haven't met."
          : "What your party knows so far: people, places, quests and recaps of earlier sessions."}
      </p>
      <Bible bible={data.bible} staff={staff} campaignId={campaign.id} onSaved={load} />
      <section className="panel">
        <div className="row-between">
          <h3>People, places & things</h3>
          <input className="search" placeholder="Search…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>
        {staff && <EntityForm campaignId={campaign.id} onSaved={load} />}
        {ENTITY_KINDS.map((kind) => {
          const list = data.entities.filter((m) => m.kind === kind && match(m));
          if (!list.length) return null;
          return (
            <div key={kind} className="entity-group">
              <h4>{KIND_LABEL[kind]} <span className="muted small">({list.length})</span></h4>
              <div className="entity-grid">
                {list.map((m) => (
                  <EntityCard key={m.id} m={m} staff={staff} campaignId={campaign.id} onSaved={load} />
                ))}
              </div>
            </div>
          );
        })}
        {!data.entities.some(match) && <p className="muted">{q ? "Nothing matches." : "Nothing recorded yet. The scribe adds NPCs, places and quests as you play."}</p>}
      </section>
      <section className="panel">
        <h3>Session recaps</h3>
        {data.summaries.length === 0 && <p className="muted">No finished sessions yet.</p>}
        {data.summaries.map((s) => (
          <details key={s.sessionId} className="recap" open={s === data.summaries[0]}>
            <summary>
              <strong>Session {s.number}</strong> <span className="muted small">{new Date(s.startedAt).toLocaleDateString()}</span>
            </summary>
            {s.recap && <p className="prewrap recap-text">{s.recap}</p>}
            {s.summary ? <p className="prewrap">{s.summary}</p> : <p className="muted small">Summary not written yet.</p>}
            {staff && <a className="small" href="#/transcript">Edit on the Transcript page (pick the session)</a>}
            {staff && <RescribeButton campaignId={campaign.id} sessionId={s.sessionId} onDone={load} />}
          </details>
        ))}
      </section>
      {data.characterNotes.length > 0 && (
        <section className="panel">
          <h3>Character developments</h3>
          <ul className="notes">
            {data.characterNotes.map((n) => (
              <li key={n.id}>
                <strong>{n.characterName}</strong>
                {n.sessionNumber ? <span className="muted small"> (session {n.sessionNumber})</span> : null}: {n.text}
                {staff && (
                  <button className="link small" onClick={() => api.deleteCharacterNote(campaign.id, n.id).then(load, fail)} title="Delete this note">
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {staff && <GlobalReadOnly campaignId={campaign.id} />}
      {staff && <SuggestGlobal campaignId={campaign.id} />}
    </main>
  );
}

function Bible({ bible, staff, campaignId, onSaved }: { bible: CampaignBible; staff: boolean; campaignId: number; onSaved: () => void }) {
  const { fail } = useGame();
  const [form, setForm] = useState(bible);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setForm(bible); }, [bible]);
  const dirty = BIBLE_ROWS.some((r) => form[r.key] !== bible[r.key]);
  const rows = BIBLE_ROWS.filter((r) => staff || (!r.staffOnly && bible[r.key].trim()));
  const save = async () => {
    setBusy(true);
    try {
      const patch: Partial<CampaignBible> = {};
      for (const r of BIBLE_ROWS) if (form[r.key] !== bible[r.key]) patch[r.key] = form[r.key];
      await api.updateBible(campaignId, patch);
      setSaved(true);
      onSaved();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel">
      <h3>Campaign bible {staff && <span className="muted small">always in the DM's mind</span>}</h3>
      {rows.length === 0 && <p className="muted">Nothing here yet.</p>}
      {staff ? (
        <div className="bible-grid">
          {rows.map((r) => (
            <label key={r.key} className={`field ${r.staffOnly ? "secret" : ""}`}>
              <span>{r.label}</span>
              <textarea rows={r.key === "timeline" || r.key === "threads" ? 5 : 3} placeholder={r.hint} value={form[r.key]} onChange={(e) => { setForm({ ...form, [r.key]: e.target.value }); setSaved(false); }} />
            </label>
          ))}
        </div>
      ) : (
        <dl className="bible-view">
          {rows.map((r) => (
            <div key={r.key}>
              <dt>{r.label}</dt>
              <dd className="prewrap">{bible[r.key]}</dd>
            </div>
          ))}
        </dl>
      )}
      {staff && (
        <div className="quick-row">
          <button className="primary" disabled={busy || !dirty} onClick={save}>Save bible</button>
          {saved && !dirty && <span className="ok small">Saved</span>}
          {bible.updatedAt && <span className="muted small">Last updated {new Date(bible.updatedAt).toLocaleString()} by {bible.updatedBy || "?"}</span>}
        </div>
      )}
    </section>
  );
}

function EntityCard({ m, staff, campaignId, onSaved }: { m: Memory; staff: boolean; campaignId: number; onSaved: () => void }) {
  const { fail } = useGame();
  const [editing, setEditing] = useState(false);
  if (editing) return <EntityForm campaignId={campaignId} existing={m} onSaved={() => { setEditing(false); onSaved(); }} onCancel={() => setEditing(false)} />;
  const meta = [
    m.attitude && `Attitude: ${m.attitude}`,
    m.status && `Status: ${m.status}`,
    m.lastSeen && `Last seen: ${m.lastSeen}`,
  ].filter(Boolean);
  return (
    <article className={`entity-card ${m.kind === "quest" && m.status ? `quest-${m.status}` : ""}`}>
      <header>
        <strong>{m.title}</strong>
        {staff && !m.knownToPlayers && <span className="tag secret" title="Players don't see this yet">hidden</span>}
        {m.voice && <span className="tag" title="The DM speaks as this NPC in this voice">🎙 {m.voice}</span>}
      </header>
      {meta.length > 0 && <p className="muted small">{meta.join(" · ")}</p>}
      {staff && m.aliases.length > 0 && <p className="muted small" title="Other names the DM and scribe match to this entry">Also known as: {m.aliases.join(", ")}</p>}
      {m.content && <p className="prewrap">{m.content}</p>}
      {staff && m.secrets && <p className="prewrap secret-text"><strong>DM-only:</strong> {m.secrets}</p>}
      {staff && (
        <footer className="muted small">
          {m.updatedBy && `Updated by ${m.updatedBy}`}{m.sessionNumber ? ` · session ${m.sessionNumber}` : ""}
          <span className="card-actions">
            <button className="link" onClick={() => setEditing(true)}>Edit</button>
            <button
              className="link danger"
              onClick={() => {
                if (window.confirm(`Delete "${m.title}" from campaign memory?`)) api.deleteEntity(campaignId, m.id).then(onSaved, fail);
              }}
            >
              Delete
            </button>
          </span>
        </footer>
      )}
    </article>
  );
}

function EntityForm({ campaignId, existing, onSaved, onCancel }: { campaignId: number; existing?: Memory; onSaved: () => void; onCancel?: () => void }) {
  const { fail, campaign } = useGame();
  const el = useElevenVoices(campaignId, campaign.ttsProvider === "elevenlabs");
  const blank: MemoryInput = { kind: "npc", title: "", content: "", secrets: "", attitude: "", lastSeen: "", status: null, knownToPlayers: true, voice: null };
  const [open, setOpen] = useState(!!existing);
  const [f, setF] = useState<MemoryInput>(existing ? { ...existing } : blank);
  const [busy, setBusy] = useState(false);
  const voices = useMemo(() => XAI_VOICES, []);
  if (!open) {
    return (
      <button className="small add-entity" onClick={() => setOpen(true)}>
        + Add an NPC, place, faction, item, quest or note
      </button>
    );
  }
  const set = (p: MemoryInput) => setF((x) => ({ ...x, ...p }));
  const save = async () => {
    setBusy(true);
    try {
      const body: MemoryInput = {
        kind: f.kind, title: f.title, content: f.content, secrets: f.secrets, attitude: f.attitude, lastSeen: f.lastSeen,
        knownToPlayers: f.knownToPlayers, voice: f.kind === "npc" ? f.voice ?? null : null, status: f.kind === "quest" ? f.status ?? "open" : null,
        aliases: f.aliases ?? [],
      };
      if (existing) await api.updateEntity(campaignId, existing.id, body);
      else await api.createEntity(campaignId, body);
      if (!existing) {
        setF(blank);
        setOpen(false);
      }
      onSaved();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="entity-form">
      <div className="form-row">
        <label className="field">
          <span>Kind</span>
          <select value={f.kind} onChange={(e) => set({ kind: e.target.value as EntityKind })}>
            {ENTITY_KINDS.map((k) => <option key={k} value={k}>{KIND_ONE[k]}</option>)}
          </select>
        </label>
        <label className="field grow">
          <span>Name</span>
          <input value={f.title ?? ""} maxLength={120} onChange={(e) => set({ title: e.target.value })} />
        </label>
        {f.kind === "quest" && (
          <label className="field">
            <span>Status</span>
            <select value={f.status ?? "open"} onChange={(e) => set({ status: e.target.value as QuestStatus })}>
              <option value="open">Open</option>
              <option value="done">Done</option>
              <option value="failed">Failed</option>
            </select>
          </label>
        )}
      </div>
      <AliasEditor aliases={f.aliases ?? []} onChange={(aliases) => set({ aliases })} />
      <label className="field">
        <span>What the party knows</span>
        <textarea rows={3} maxLength={3000} value={f.content ?? ""} onChange={(e) => set({ content: e.target.value })} />
      </label>
      <label className="field secret">
        <span>DM-only secrets</span>
        <textarea rows={2} maxLength={3000} value={f.secrets ?? ""} onChange={(e) => set({ secrets: e.target.value })} />
      </label>
      <div className="form-row">
        {f.kind === "npc" && (
          <label className="field grow">
            <span>Attitude to the party</span>
            <input value={f.attitude ?? ""} maxLength={300} onChange={(e) => set({ attitude: e.target.value })} />
          </label>
        )}
        <label className="field grow">
          <span>Last seen</span>
          <input value={f.lastSeen ?? ""} maxLength={300} onChange={(e) => set({ lastSeen: e.target.value })} />
        </label>
        {f.kind === "npc" && (
          <label className="field">
            <span>NPC voice</span>
            <select value={f.voice ?? ""} onChange={(e) => set({ voice: e.target.value || null })}>
              <option value="">DM's voice</option>
              <optgroup label="xAI">
                {voices.map((v) => <option key={v.id} value={v.id}>{v.id} ({v.tags})</option>)}
              </optgroup>
              {el.voices.length > 0 && (
                <optgroup label="ElevenLabs (your account)">
                  {el.voices.map((v) => <option key={v.id} value={`el:${v.id}`}>{v.name}</option>)}
                </optgroup>
              )}
              {f.voice?.startsWith("el:") && !el.voices.some((v) => `el:${v.id}` === f.voice) && <option value={f.voice}>{f.voice}</option>}
            </select>
            {f.voice && !f.voice.startsWith("el:") && (
              <button type="button" className="small" title="Hear a short sample of this voice" onClick={() => { void new Audio(api.voicePreviewUrl(f.voice!)).play().catch(() => undefined); }}>▶ Preview</button>
            )}
          </label>
        )}
      </div>
      <label className="check-inline">
        <input type="checkbox" checked={f.knownToPlayers !== false} onChange={() => set({ knownToPlayers: !(f.knownToPlayers !== false) })} /> Players know about this (shown on their Memory tab)
      </label>
      <div className="quick-row">
        <button className="primary" disabled={busy || !f.title?.trim()} onClick={save}>{existing ? "Save" : "Add"}</button>
        <button disabled={busy} onClick={() => (onCancel ? onCancel() : setOpen(false))}>Cancel</button>
      </div>
    </div>
  );
}

function SuggestGlobal({ campaignId }: { campaignId: number }) {
  const { fail } = useGame();
  const [category, setCategory] = useState<GlobalMemoryCategory>("lesson");
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);
  return (
    <section className="panel">
      <h3>Suggest to Admin</h3>
      <p className="muted small">House rules, style tips or lessons that every campaign's DM should follow. The Admin reviews suggestions.</p>
      <div className="form-row">
        <select value={category} onChange={(e) => setCategory(e.target.value as GlobalMemoryCategory)}>
          <option value="rule">House rule</option>
          <option value="style">Style</option>
          <option value="lesson">Lesson</option>
        </select>
        <input className="grow" value={text} maxLength={1000} placeholder="e.g. Always describe the weather when the party travels." onChange={(e) => { setText(e.target.value); setSent(false); }} />
        <button
          disabled={!text.trim()}
          onClick={() =>
            api.suggestGlobal(campaignId, category, text.trim()).then(() => {
              setText("");
              setSent(true);
            }, fail)
          }
        >
          Suggest
        </button>
        {sent && <span className="ok small">Sent to the Admin</span>}
      </div>
    </section>
  );
}

const GLOBAL_LABEL: Record<GlobalMemoryCategory, string> = { rule: "House rule", style: "Style", lesson: "Lesson" };

/** What every DM is told (the Admin's global DM memory), read-only for Hosts and Co-hosts. */
function GlobalReadOnly({ campaignId }: { campaignId: number }) {
  const [entries, setEntries] = useState<GlobalMemoryEntry[] | null>(null);
  useEffect(() => {
    api.campaignGlobalMemory(campaignId).then((r) => setEntries(r.entries), () => setEntries([]));
  }, [campaignId]);
  return (
    <section className="panel">
      <h3>Global DM memory <span className="muted small">(read-only)</span></h3>
      <p className="muted small">Every campaign's DM always follows these. Only the Admin can edit them; suggest changes below.</p>
      {entries === null ? <p className="muted">Loading…</p> : entries.length === 0 ? <p className="muted small">Nothing yet.</p> : (
        <ol className="global-list">
          {entries.map((e) => (
            <li key={e.id}>
              <span className={`tag cat-${e.category}`}>{GLOBAL_LABEL[e.category]}</span> {e.text}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/** Other names for an entry ("the ferrywoman"): chips with × to remove, and a box to add one. */
function AliasEditor({ aliases, onChange }: { aliases: string[]; onChange: (a: string[]) => void }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const a = draft.trim();
    if (!a || aliases.some((x) => x.toLowerCase() === a.toLowerCase()) || aliases.length >= 8) return;
    onChange([...aliases, a.slice(0, 80)]);
    setDraft("");
  };
  return (
    <div className="field alias-editor">
      <span title="The DM and scribe treat these names as this entry (so it keeps one picture and one note)">Also known as</span>
      <div className="alias-chips">
        {aliases.map((a) => (
          <span key={a} className="tag alias-chip">{a}
            <button type="button" className="icon" aria-label={`Remove ${a}`} onClick={() => onChange(aliases.filter((x) => x !== a))}>×</button>
          </span>
        ))}
        {aliases.length < 8 && (
          <input value={draft} maxLength={80} placeholder="Add another name, e.g. the ferrywoman" onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} onBlur={add} />
        )}
      </div>
    </div>
  );
}

/** Host/Co-host: have the scribe re-read a finished session from its transcript (e.g. after its summary failed). */
function RescribeButton({ campaignId, sessionId, onDone }: { campaignId: number; sessionId: number; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  return (
    <span className="rescribe">
      {" · "}
      <button type="button" className="link small" disabled={busy}
        title="The scribe re-reads this session's transcript and rewrites its summary (a few cents on the Background model). It won't change the current state."
        onClick={async () => {
          setBusy(true); setMsg("");
          try {
            const s = await api.rescribe(campaignId, sessionId);
            setMsg(s.summaryStatus === "done" ? "Summary rewritten." : s.summaryStatus === "skipped" ? "Too little was said to summarise." : "The scribe couldn't finish; it will retry.");
            onDone();
          } catch (e) { setMsg((e as Error).message); } finally { setBusy(false); }
        }}>{busy ? "Re-reading…" : "Re-run summary"}</button>
      {msg && <span className="muted small"> {msg}</span>}
    </span>
  );
}
